import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  PageHeader, KPIGrid, DataTable, StandardModal, StatusBadge,
  FilterPills, Alert, Btn, FormField,
  inputStyle, textareaStyle, selectStyle,
  colors, spacing, typography, radius,
} from "../design-system";

const BASE = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
const API = `${BASE}/accreditation-renewals`;

function EntityTypeBadge({ type }) {
  const isCB = type === "cb";
  return (
    <span style={{
      background: isCB ? colors.primaryLight : "#f3e8ff",
      color:      isCB ? colors.primary      : colors.purple,
      padding: "2px 10px", borderRadius: radius.pill,
      fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold,
    }}>
      {type?.toUpperCase()}
    </span>
  );
}

export default function AccreditationRenewals() {
  const [renewals, setRenewals]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType]   = useState("all");
  const [showCreate, setShowCreate]   = useState(false);
  const [showConfirm, setShowConfirm] = useState(null);
  const [showEvidence, setShowEvidence] = useState(null);
  const [createForm, setCreateForm]   = useState({ entity_type: "cb", entity_id: "", renewal_due_date: "", notes: "" });
  const [decisionNotes, setDecisionNotes] = useState("");
  const [saving, setSaving]           = useState(false);
  const [msg, setMsg]                 = useState("");
  const [msgType, setMsgType]         = useState("success");
  const evidenceRef = useRef();

  const token    = localStorage.getItem("token");
  const userRole = JSON.parse(localStorage.getItem("user") || "{}").role;
  const isSuperAdmin = userRole === "super_admin";
  const headers  = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const fetchRenewals = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.set("renewal_status", filterStatus);
      if (filterType !== "all") params.set("entity_type", filterType);
      const res = await fetch(`${API}/?${params}`, { headers });
      if (!res.ok) throw new Error("Failed to load renewals");
      setRenewals(await res.json());
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType]);

  useEffect(() => { fetchRenewals(); }, [fetchRenewals]);

  const flash = (m, t = "success") => { setMsg(m); setMsgType(t); setTimeout(() => setMsg(""), 3500); };

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/`, {
        method: "POST", headers,
        body: JSON.stringify({
          entity_type: createForm.entity_type,
          entity_id:   parseInt(createForm.entity_id),
          renewal_due_date: createForm.renewal_due_date,
          notes: createForm.notes || null,
        }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash("Renewal record created.");
      setShowCreate(false);
      setCreateForm({ entity_type: "cb", entity_id: "", renewal_due_date: "", notes: "" });
      fetchRenewals();
    } catch (e) { flash("Error: " + e.message, "error"); }
    finally { setSaving(false); }
  }

  async function handleAction(id, action) {
    setSaving(true);
    try {
      const body = decisionNotes ? { notes: decisionNotes } : {};
      const res = await fetch(`${API}/${id}/${action}`, { method: "PATCH", headers, body: JSON.stringify(body) });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash(`Renewal ${action}d successfully.`);
      setShowConfirm(null);
      setDecisionNotes("");
      fetchRenewals();
    } catch (e) { flash("Error: " + e.message, "error"); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm("Permanently delete this renewal record? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API}/${id}/`, { method: "DELETE", headers });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash("Renewal deleted.");
      fetchRenewals();
    } catch (e) { flash("Error: " + e.message, "error"); }
  }

  async function handleEvidenceUpload(id) {
    const file = evidenceRef.current?.files?.[0];
    if (!file) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/${id}/upload-evidence`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash("Evidence uploaded.");
      setShowEvidence(null);
      fetchRenewals();
    } catch (e) { flash("Error: " + e.message, "error"); }
    finally { setSaving(false); }
  }

  const dueSoon = renewals.filter(r => {
    if (!r.renewal_due_date || r.renewal_status === "approved") return false;
    const diff = (new Date(r.renewal_due_date) - new Date()) / (1000 * 86400);
    return diff <= 90 && diff >= 0;
  }).length;

  const kpis = [
    { label: "Total",         value: renewals.length,                                    icon: "🔄", color: colors.primary  },
    { label: "Open",          value: renewals.filter(r => r.renewal_status === "open").length, icon: "📂", color: colors.info },
    { label: "Due ≤90 Days",  value: dueSoon,                                            icon: "⏰", color: dueSoon > 0 ? colors.warning : colors.success },
    { label: "Pending Review", value: renewals.filter(r => ["submitted","under_review"].includes(r.renewal_status)).length, icon: "🔎", color: colors.purple },
  ];

  const statusOptions = [
    { value: "all",         label: "All Status" },
    { value: "not_due",     label: "Not Due" },
    { value: "open",        label: "Open" },
    { value: "submitted",   label: "Submitted" },
    { value: "under_review", label: "Under Review" },
    { value: "approved",    label: "Approved" },
    { value: "rejected",    label: "Rejected" },
  ];

  const typeOptions = [
    { value: "all",     label: "All Types" },
    { value: "cb",      label: "Cert Body" },
    { value: "auditor", label: "Auditor" },
  ];

  const columns = [
    { key: "id",          label: "ID",      render: v => <span style={{ color: colors.textMuted, fontFamily: "monospace" }}>#{v}</span> },
    { key: "entity_type", label: "Type",    render: v => {
      const isGB = v === "cb";
      return (
        <span style={{ background: isGB ? colors.primaryLight : "#f3e8ff", color: isGB ? colors.primary : colors.purple, padding: "2px 10px", borderRadius: radius.pill, fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold }}>
          {v?.toUpperCase()}
        </span>
      );
    }},
    { key: "entity_id",   label: "Entity",  render: v => `Entity #${v}` },
    { key: "renewal_due_date", label: "Due Date", render: (v, row) => {
      const isOverdue = v && new Date(v) < new Date() && row.renewal_status !== "approved";
      return (
        <span style={{ color: isOverdue ? colors.danger : colors.textPrimary, fontWeight: isOverdue ? typography.fontWeights.semibold : typography.fontWeights.regular }}>
          {v ? new Date(v).toLocaleDateString() : "—"}
          {isOverdue && <span style={{ marginLeft: 6, background: colors.dangerLight, color: colors.danger, padding: "1px 6px", borderRadius: radius.xs, fontSize: typography.fontSizes.xs }}>Overdue</span>}
        </span>
      );
    }},
    { key: "renewal_window_start", label: "Window", render: (v, row) => (
      <span style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted }}>
        {v ? new Date(v).toLocaleDateString() : "—"} → {row.renewal_window_end ? new Date(row.renewal_window_end).toLocaleDateString() : "—"}
      </span>
    )},
    { key: "renewal_status", label: "Status", render: v => <StatusBadge status={v} /> },
    { key: "renewal_evidence_url", label: "Evidence", render: v =>
      v ? <a href={v} target="_blank" rel="noopener noreferrer" style={{ color: colors.primary }}>View</a>
        : <span style={{ color: colors.textMuted }}>None</span>
    },
    {
      key: "_actions",
      label: "",
      align: "right",
      render: (_, r) => (
        <div style={{ display: "flex", gap: spacing.xs, justifyContent: "flex-end" }}>
          {["open", "not_due"].includes(r.renewal_status) && (
            <Btn size="sm" variant="outline" onClick={() => setShowConfirm({ id: r.id, action: "submit", label: "Submit" })}>Submit</Btn>
          )}
          {!["approved", "rejected"].includes(r.renewal_status) && (
            <Btn size="sm" variant="ghost" onClick={() => setShowEvidence(r)}>Evidence</Btn>
          )}
          {isSuperAdmin && r.renewal_status === "submitted" && (
            <Btn size="sm" variant="ghost" onClick={() => setShowConfirm({ id: r.id, action: "under-review", label: "Under Review" })}>Review</Btn>
          )}
          {isSuperAdmin && ["submitted", "under_review"].includes(r.renewal_status) && (
            <>
              <Btn size="sm" variant="success" onClick={() => { setShowConfirm({ id: r.id, action: "approve", label: "Approve" }); setDecisionNotes(""); }}>Approve</Btn>
              <Btn size="sm" variant="danger"  onClick={() => { setShowConfirm({ id: r.id, action: "reject",  label: "Reject" });  setDecisionNotes(""); }}>Reject</Btn>
            </>
          )}
          {isSuperAdmin && (
            <Btn size="sm" variant="danger" onClick={() => handleDelete(r.id)}>Delete</Btn>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Accreditation Renewals"
        subtitle="Manage renewal workflows for CB and Auditor accreditations."
        actions={isSuperAdmin && <Btn onClick={() => setShowCreate(true)}>+ Create Renewal</Btn>}
      />

      <KPIGrid kpis={kpis} columns={4} />

      {msg && <Alert type={msgType} message={msg} onClose={() => setMsg("")} />}

      {/* Filters */}
      <div style={{ display: "flex", gap: spacing.md, marginBottom: spacing.lg, flexWrap: "wrap", alignItems: "center" }}>
        <FilterPills options={statusOptions} value={filterStatus} onChange={setFilterStatus} />
        <FilterPills options={typeOptions}   value={filterType}   onChange={setFilterType} color={colors.purple} />
      </div>

      <DataTable columns={columns} rows={renewals} loading={loading} emptyMessage="No renewals found." keyField="id" />

      {/* Create Modal */}
      {showCreate && (
        <StandardModal
          title="Create Renewal Record"
          onClose={() => setShowCreate(false)}
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Btn>
              <Btn type="submit" form="create-renewal-form" loading={saving}>Create Renewal</Btn>
            </>
          }
        >
          <form id="create-renewal-form" onSubmit={handleCreate}>
            <div style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
              <FormField label="Entity Type" required>
                <select value={createForm.entity_type} onChange={e => setCreateForm(f => ({ ...f, entity_type: e.target.value }))} style={selectStyle}>
                  <option value="cb">Certification Body (CB)</option>
                  <option value="auditor">Auditor</option>
                </select>
              </FormField>
              <FormField label="Entity ID" required helper="The accreditation record ID">
                <input required type="number" min="1" value={createForm.entity_id} onChange={e => setCreateForm(f => ({ ...f, entity_id: e.target.value }))} style={inputStyle} />
              </FormField>
              <FormField label="Renewal Due Date" required>
                <input required type="datetime-local" value={createForm.renewal_due_date} onChange={e => setCreateForm(f => ({ ...f, renewal_due_date: e.target.value }))} style={inputStyle} />
              </FormField>
              <FormField label="Notes">
                <textarea value={createForm.notes} onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))} rows={3} style={textareaStyle} />
              </FormField>
            </div>
          </form>
        </StandardModal>
      )}

      {/* Confirm Action Modal */}
      {showConfirm && (
        <StandardModal
          title={`${showConfirm.label} Renewal #${showConfirm.id}`}
          onClose={() => { setShowConfirm(null); setDecisionNotes(""); }}
          danger={showConfirm.action === "reject"}
          footer={
            <>
              <Btn variant="secondary" onClick={() => { setShowConfirm(null); setDecisionNotes(""); }}>Cancel</Btn>
              <Btn
                variant={showConfirm.action === "reject" ? "danger" : "primary"}
                onClick={() => handleAction(showConfirm.id, showConfirm.action)}
                loading={saving}
              >
                {showConfirm.label}
              </Btn>
            </>
          }
        >
          <p style={{ color: colors.textSecondary, marginBottom: spacing.md }}>
            {showConfirm.action === "approve" && "Approving this renewal will extend the linked accreditation by 36 months."}
            {showConfirm.action === "reject"  && "Are you sure you want to reject this renewal application?"}
            {showConfirm.action === "submit"  && "Submit this renewal application for ICSC review?"}
            {showConfirm.action === "under-review" && "Mark this renewal as under review?"}
          </p>
          {["approve", "reject"].includes(showConfirm.action) && (
            <FormField label="Decision Notes">
              <textarea value={decisionNotes} onChange={e => setDecisionNotes(e.target.value)} rows={3} style={textareaStyle} />
            </FormField>
          )}
        </StandardModal>
      )}

      {/* Evidence Upload Modal */}
      {showEvidence && (
        <StandardModal
          title={`Upload Evidence — Renewal #${showEvidence.id}`}
          onClose={() => setShowEvidence(null)}
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowEvidence(null)}>Cancel</Btn>
              <Btn onClick={() => handleEvidenceUpload(showEvidence.id)} loading={saving}>Upload</Btn>
            </>
          }
        >
          <p style={{ color: colors.textSecondary, marginBottom: spacing.md }}>
            Upload supporting documents for this renewal application.
          </p>
          <input ref={evidenceRef} type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" style={{ display: "block", fontSize: typography.fontSizes.md }} />
        </StandardModal>
      )}
    </div>
  );
}
